// Shared SEO head helpers. Every route uses `seoHead()` so every page ships
// a self-referencing canonical + unique og:title / og:description / og:url
// pointing at the primary domain (coffmancovelodging.com). Never reference
// the lovable.app domain in canonical / og / twitter URLs.

export const SITE_ORIGIN = "https://coffmancovelodging.com";

// Primary Open Graph image (1200×630 — Facebook/LinkedIn/Slack sweet spot).
export const DEFAULT_OG_IMAGE =
  "https://coffmancovelodging.com/__l5e/assets-v1/cc2bcf31-4b36-4a87-9294-2c2f3505dae5/og-coffman-cove-v2.jpg";

// Smaller landscape variant (~1.91:1) — same content, lower bandwidth for
// crawlers that fetch under a size budget (iMessage, some chat apps).
const DEFAULT_OG_IMAGE_MEDIUM =
  "https://coffmancovelodging.com/__l5e/assets-v1/7cf8f13b-0cb8-4b92-a140-936b18a87bf4/og-coffman-cove-800x418.jpg";

// Square 1:1 variant — WhatsApp / iMessage sometimes prefer square previews.
const DEFAULT_OG_IMAGE_SQUARE =
  "https://coffmancovelodging.com/__l5e/assets-v1/6c07f84c-2275-4778-996d-c4e0d3d5d863/og-coffman-cove-600x600.jpg";

export interface OgImageVariant {
  url: string;
  width: number;
  height: number;
  type?: string;
  alt?: string;
}

// Default variant set for pages that don't override the image. Ordered by
// preference: crawlers that respect ordering (Facebook, LinkedIn) pick the
// first supported entry; others (WhatsApp, iMessage) inspect all and pick
// the closest fit to their target aspect ratio.
export const DEFAULT_OG_IMAGE_VARIANTS: OgImageVariant[] = [
  { url: DEFAULT_OG_IMAGE, width: 1200, height: 630, type: "image/jpeg" },
  { url: DEFAULT_OG_IMAGE_MEDIUM, width: 800, height: 418, type: "image/jpeg" },
  { url: DEFAULT_OG_IMAGE_SQUARE, width: 600, height: 600, type: "image/jpeg" },
];

type MetaEntry =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string }
  | { charSet: string };

type LinkEntry = { rel: string; href: string; crossOrigin?: "" | "anonymous" | "use-credentials" };

type ScriptEntry = { type: string; children: string };

export interface SeoHeadInput {
  path: string;
  title: string;
  description: string;
  /**
   * Single image URL. If provided, replaces the default variant set with a
   * single og:image entry. Use `imageVariants` when a route ships multiple
   * sizes of the same artwork.
   */
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageType?: string;
  /**
   * Multiple sizes of the same image. Emitted in order — crawlers pick the
   * best fit. Overrides `image` when provided.
   */
  imageVariants?: OgImageVariant[];
  type?: "website" | "article" | "product";
  extraMeta?: MetaEntry[];
  extraLinks?: LinkEntry[];
  scripts?: ScriptEntry[];
}

const DEFAULT_IMAGE_TYPE = "image/jpeg";

function resolveVariants(input: SeoHeadInput): OgImageVariant[] {
  if (input.imageVariants && input.imageVariants.length) {
    return input.imageVariants;
  }
  if (input.image) {
    return [
      {
        url: input.image,
        width: input.imageWidth ?? 1200,
        height: input.imageHeight ?? 630,
        type: input.imageType ?? DEFAULT_IMAGE_TYPE,
        alt: input.imageAlt,
      },
    ];
  }
  return DEFAULT_OG_IMAGE_VARIANTS;
}

/**
 * Sitewide build tag appended to share-image URLs as `?v=…` when a route
 * doesn't supply its own version. Bumping this via the `VITE_BUILD_TAG`
 * env var (or letting the default change per release) forces every share
 * crawler — Facebook, LinkedIn, iMessage, Slack, X — to re-fetch og:image
 * assets on the next scrape instead of serving whatever they cached from
 * the previous shape of the artwork.
 */
export const BUILD_TAG: string =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: Record<string, string | undefined> }).env
      ?.VITE_BUILD_TAG) ||
  "r1";

/**
 * Append `?v=<version>` (or `&v=…` if the URL already carries a query
 * string) to force share crawlers to refetch a cached preview. Empty /
 * falsy versions are a no-op so callers can opt out per-URL. Idempotent:
 * an existing `v=` param is replaced, not duplicated.
 */
export function withVersion(url: string, version?: string | null): string {
  if (!version) return url;
  if (/[?&]v=[^&]*/.test(url)) {
    return url.replace(/([?&])v=[^&]*/, `$1v=${encodeURIComponent(version)}`);
  }
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}v=${encodeURIComponent(version)}`;
}

/**
 * Promote a site-relative path (or an imported asset pointer with a `.url`
 * field) into an absolute URL rooted at `SITE_ORIGIN`. Idempotent — if the
 * input is already absolute (http/https) it's returned unchanged.
 *
 * Route heads MUST use this helper — or `seoHead()`, which calls it —
 * whenever they set `og:image`, `twitter:image`, or a JSON-LD `image`
 * field. Crawlers reject relative paths, and hand-concatenating
 * `${SITE_ORIGIN}${asset.url}` in every route lets the origin drift
 * (missing slash, wrong domain, http vs https) route-by-route.
 *
 * Accepts either a string or an asset-pointer object shaped like the
 * `*.asset.json` files under `src/assets/` (`{ url, asset_id? }`).
 */
export function absoluteUrl(input: string | { url: string }): string {
  const raw = typeof input === "string" ? input : input.url;
  if (/^https?:\/\//i.test(raw)) return raw;
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return `${SITE_ORIGIN}${path}`;
}

/**
 * Absolute URL for share-image use, with a `?v=` cache-buster appended so
 * crawlers refetch when the underlying asset changes.
 *
 * Version resolution, highest priority first:
 *   1. Explicit `version` argument.
 *   2. `asset_id` on an imported `*.asset.json` pointer — changes whenever
 *      the asset is re-uploaded, so preview caches invalidate automatically.
 *   3. Sitewide `BUILD_TAG` (bump `VITE_BUILD_TAG` to force a refresh).
 *
 * Use at every og:image / twitter:image / JSON-LD image call site so no
 * route can drift out of sync with `SITE_ORIGIN` or ship a URL without a
 * cache-buster.
 */
export function absoluteImageUrl(
  input: string | { url: string; asset_id?: string },
  version?: string | null,
): string {
  const abs = absoluteUrl(input);
  const assetId =
    typeof input === "object" && input && "asset_id" in input
      ? input.asset_id
      : undefined;
  const v = version ?? assetId ?? BUILD_TAG;
  return withVersion(abs, v);
}

function absolutize(url: string): string {
  return absoluteUrl(url);
}

export function seoHead(input: SeoHeadInput) {
  const {
    path,
    title,
    description,
    imageAlt,
    type = "website",
    extraMeta = [],
    extraLinks = [],
    scripts,
  } = input;
  const url = absoluteUrl(path);
  const variants = resolveVariants(input).map((v) => ({ ...v, url: absolutize(v.url) }));
  // Always ship an alt so Twitter / Slack / iMessage announce the image
  // consistently — fall back to the page title when the caller didn't
  // supply one.
  const resolvedImageAlt = imageAlt ?? title;

  // Twitter uses a single image tag — pick the largest landscape variant
  // (closest to Twitter's summary_large_image 2:1 recommendation).
  const twitterImage =
    variants
      .filter((v) => v.width >= v.height)
      .sort((a, b) => b.width - a.width)[0] ?? variants[0];

  const meta: MetaEntry[] = [
    { title },
    { name: "description", content: description },
    // Open Graph — Facebook, LinkedIn, iMessage, Slack, Discord.
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:type", content: type },
    { property: "og:locale", content: "en_US" },
    { property: "og:site_name", content: "Coffman Cove Lodging" },
  ];

  // Emit each variant as an ordered og:image block. The og spec pairs
  // width/height/type/alt with the most recent og:image entry, so keep them
  // grouped per variant.
  for (const v of variants) {
    meta.push({ property: "og:image", content: v.url });
    meta.push({ property: "og:image:secure_url", content: v.url });
    meta.push({ property: "og:image:type", content: v.type ?? DEFAULT_IMAGE_TYPE });
    meta.push({ property: "og:image:width", content: String(v.width) });
    meta.push({ property: "og:image:height", content: String(v.height) });
    meta.push({ property: "og:image:alt", content: v.alt ?? resolvedImageAlt });
  }

  meta.push(
    // Twitter / X — summary_large_image mirrors the OG image at ~2:1.
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: twitterImage.url },
    { name: "twitter:image:alt", content: twitterImage.alt ?? resolvedImageAlt },
  );
  meta.push(...extraMeta);

  // Crawlers require absolute URLs for the SEO-critical link rels
  // (`canonical`, `alternate`, `next`, `prev`). Route any caller-supplied
  // href through `absoluteUrl` so a route can pass a bare path (e.g.
  // `/fishing?lang=de` or `/rss.xml`) and still emit the fully qualified
  // form crawlers expect.
  const SEO_LINK_RELS = new Set(["canonical", "alternate", "next", "prev"]);
  const normalizedExtraLinks = extraLinks.map((l) =>
    SEO_LINK_RELS.has(l.rel) ? { ...l, href: absoluteUrl(l.href) } : l,
  );
  const links: LinkEntry[] = [
    { rel: "canonical", href: absoluteUrl(url) },
    ...normalizedExtraLinks,
  ];

  const head: { meta: MetaEntry[]; links: LinkEntry[]; scripts?: ScriptEntry[] } = {
    meta,
    links,
  };
  if (scripts && scripts.length) head.scripts = scripts;
  return head;
}
